/**
 * The fire panel (docs/ux.md §6.3): the target, who can shoot at it with how many shots, the
 * odds and what a hit would do, and the two ways to fire. It floats over the board's corner,
 * so the board stays usable: tapping an own shooter checks it in the panel.
 */
import type { GameEvent, GameState } from '@brig/rules-engine';
import type { TFunction } from 'i18next';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { committed, shotsLeft, staysInPort, toFinish, totalShots, type FireDraft } from './fire.ts';
import * as styles from './Game.css.ts';
import { HudButton } from './HudButton.tsx';
import { Segments } from './ShipInspector.tsx';
import { Icon } from './icons.tsx';
import { current } from './history.ts';
import type { TurnSend, TurnSnapshot } from './useTurn.ts';

/** What one hit with `shots` shots would do to the target (U-FIRE-05). */
function outcome(t: TFunction, state: GameState, target: string, shots: number): string {
  const { left, max, now } = toFinish(state, target);
  const ship = state.ships.some((s) => s.id === target);
  if (shots >= left) return ship ? t('game.fire.sinks') : t('game.fire.destroys');
  return ship
    ? t('game.fire.holesAfter', { n: now + shots, max })
    : t('game.fire.hitsAfter', { n: now + shots, max });
}

/** The last shot of the history, if it was at this target: the panel shows its result. */
function lastShotAt(events: readonly (readonly GameEvent[])[], target: string) {
  const shot = events.at(-1)?.find((e) => e.type === 'shot');
  return shot?.type === 'shot' && shot.targetId === target ? shot : null;
}

export function FirePanel({
  snapshot,
  send,
  nameOf,
}: {
  snapshot: TurnSnapshot;
  send: TurnSend;
  nameOf: (id: string) => string;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const noteId = useId();
  const fire: FireDraft | null = snapshot.context.fire;
  const history = snapshot.context.history;
  const lastShot = fire ? lastShotAt(history.events, fire.target) : null;
  if (!fire) return null;
  const state = current(history);
  const { target } = fire;
  const finish = toFinish(state, target);
  const isShip = state.ships.some((s) => s.id === target);
  const shots = totalShots(fire);
  const shooters = committed(fire).length;
  const firing = snapshot.context.queue.length > 0;
  const volley = shooters >= state.ruleset.volleyMinShooters;

  return (
    <div
      className={styles.firePanel}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      data-testid="fire-panel"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          send({ type: 'CLOSE_FIRE' });
        }
      }}
    >
      <div className={styles.fireHead}>
        <h2 id={titleId} className={styles.fireTitle}>
          <Icon name={volley ? 'volley' : 'cannon'} size={18} />
          {t('game.fire.title', { target: nameOf(target) })}
        </h2>
        <span
          className={styles.fireState}
          role="img"
          aria-label={
            isShip
              ? t('game.fire.holes', { n: finish.now, max: finish.max })
              : t('game.fire.fortHits', { n: finish.now, max: finish.max })
          }
        >
          <Segments n={finish.now} max={finish.max} tone="hole" />
        </span>
        {lastShot ? (
          <strong className={styles.shotResult} role="status">
            {lastShot.hit ? t('game.fire.hit') : t('game.fire.miss')}
          </strong>
        ) : null}
        <HudButton
          icon="close"
          size="sm"
          label={t('game.fire.close')}
          keyHint="Esc"
          onClick={() => {
            send({ type: 'CLOSE_FIRE' });
          }}
        />
      </div>
      <ul className={styles.shooters}>
        {Object.keys(fire.shots).map((id) => {
          const n = fire.shots[id] ?? 0;
          const max = shotsLeft(state, id);
          const name = nameOf(id);
          const inPort = n > 0 && staysInPort(state, id);
          return (
            <li key={id} className={styles.shooter}>
              <label className={styles.shooterName}>
                <input
                  type="checkbox"
                  checked={n > 0}
                  disabled={firing}
                  onChange={(e) => {
                    send({ type: 'SET_SHOTS', id, shots: e.target.checked ? max : 0 });
                  }}
                />
                {name}
              </label>
              {inPort ? (
                // R-PORT-04: the one side effect worth a mark; the words on hover.
                <span className={styles.shooterNote} title={t('game.fire.staysInPort')}>
                  <Icon name="anchor" size={14} />
                  <span className={styles.srOnly}>{t('game.fire.staysInPort')}</span>
                </span>
              ) : null}
              {max > 1 ? (
                <span className={styles.stepper}>
                  <button
                    type="button"
                    className={styles.button}
                    aria-label={t('game.fire.fewer', { unit: name })}
                    disabled={firing || n === 0}
                    onClick={() => {
                      send({ type: 'SET_SHOTS', id, shots: n - 1 });
                    }}
                  >
                    −
                  </button>
                  <span aria-live="polite">{n}</span>
                  <button
                    type="button"
                    className={styles.button}
                    aria-label={t('game.fire.more', { unit: name })}
                    disabled={firing || n >= max}
                    onClick={() => {
                      send({ type: 'SET_SHOTS', id, shots: n + 1 });
                    }}
                  >
                    +
                  </button>
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>

      <div className={styles.fireActions}>
        {shooters === 0 ? (
          <p className={styles.prompt}>{t('game.fire.none')}</p>
        ) : (
          // What a hit would do.
          <div className={styles.hint} data-testid="fire-odds">
            {outcome(t, state, target, volley ? shots : 1)}
          </div>
        )}
        <span id={noteId} className={styles.srOnly}>
          {t('game.fire.noReturn')}
        </span>
        <span className={styles.fireButtons}>
          {firing ? (
            <button
              type="button"
              className={styles.primary}
              onClick={() => {
                send({ type: 'STOP_FIRE' });
              }}
            >
              {t('game.fire.stop')}
            </button>
          ) : (
            <>
              {/* One by one only means something with more than one shot to fire. */}
              {shots > 1 ? (
                <button
                  type="button"
                  className={styles.button}
                  aria-describedby={noteId}
                  title={t('game.fire.noReturn')}
                  onClick={() => {
                    send({ type: 'FIRE_SEQUENCE' });
                  }}
                >
                  {t('game.fire.oneByOne')}
                </button>
              ) : null}
              {volley ? (
                <button
                  type="button"
                  className={styles.danger}
                  aria-describedby={noteId}
                  title={t('game.fire.noReturn')}
                  onClick={() => {
                    send({ type: 'VOLLEY' });
                  }}
                >
                  <Icon name="volley" size={18} />
                  {t('game.fire.volley', { n: shots })}
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.danger}
                  disabled={shooters === 0}
                  aria-describedby={noteId}
                  title={t('game.fire.noReturn')}
                  onClick={() => {
                    send({ type: 'SHOOT' });
                  }}
                >
                  <Icon name="cannon" size={18} />
                  {t('game.fire.shoot')}
                </button>
              )}
            </>
          )}
        </span>
      </div>
    </div>
  );
}
